import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent } from '@mui/material';
import { FiX, FiExternalLink } from 'react-icons/fi';

const TutorialVideoPlayer = ({ open, onClose, tutorial }) => {
  const [embedUrl, setEmbedUrl] = useState('');
  
  useEffect(() => {
    if (tutorial?.link) {
      const url = getEmbedUrl(tutorial.link);
      setEmbedUrl(url);
    }
  }, [tutorial]);

  const getEmbedUrl = (url) => {
    if (!url) return '';

    // YouTube - multiple patterns
    const youtubePatterns = [
      /(?:https?:\/\/)?(?:www\.)?youtube\.com\/watch\?v=([a-zA-Z0-9_-]{11})/,
      /(?:https?:\/\/)?(?:www\.)?youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/,
      /(?:https?:\/\/)?(?:www\.)?youtube\.com\/v\/([a-zA-Z0-9_-]{11})/,
      /(?:https?:\/\/)?youtu\.be\/([a-zA-Z0-9_-]{11})/,
    ];
    
    for (const pattern of youtubePatterns) {
      const match = url.match(pattern);
      if (match && match[1]) {
        return `https://www.youtube.com/embed/${match[1]}?autoplay=1&rel=0`;
      }
    }

    // Vimeo
    const vimeoPatterns = [
      /(?:https?:\/\/)?(?:www\.)?vimeo\.com\/(\d+)/,
      /(?:https?:\/\/)?(?:www\.)?vimeo\.com\/video\/(\d+)/,
      /(?:https?:\/\/)?player\.vimeo\.com\/video\/(\d+)/,
    ];
    
    for (const pattern of vimeoPatterns) {
      const match = url.match(pattern);
      if (match && match[1]) {
        return `https://player.vimeo.com/video/${match[1]}?autoplay=1`;
      }
    }

    // Loom
    const loomPatterns = [
      /(?:https?:\/\/)?(?:www\.)?loom\.com\/share\/([a-zA-Z0-9]+)/,
      /(?:https?:\/\/)?(?:www\.)?loom\.com\/embed\/([a-zA-Z0-9]+)/,
    ];
    
    for (const pattern of loomPatterns) {
      const match = url.match(pattern);
      if (match && match[1]) {
        return `https://www.loom.com/embed/${match[1]}`;
      }
    }

    // Wistia
    const wistiaPatterns = [
      /(?:https?:\/\/)?(?:www\.)?wistia\.com\/medias\/([a-zA-Z0-9]+)/,
      /(?:https?:\/\/)?fast\.wistia\.net\/embed\/iframe\/([a-zA-Z0-9]+)/,
    ];
    
    for (const pattern of wistiaPatterns) {
      const match = url.match(pattern);
      if (match && match[1]) {
        return `https://fast.wistia.net/embed/iframe/${match[1]}`;
      }
    }

    // Dailymotion
    const dailymotionPattern = /(?:https?:\/\/)?(?:www\.)?dailymotion\.com\/video\/([a-zA-Z0-9]+)/;
    const dailymotionMatch = url.match(dailymotionPattern);
    if (dailymotionMatch && dailymotionMatch[1]) {
      return `https://www.dailymotion.com/embed/video/${dailymotionMatch[1]}`;
    }

    // If it's a direct video file or already an embed URL, return as is
    if (isDirectVideoLink(url) || url.includes('/embed/') || url.includes('player.')) {
      return url;
    }

    return '';
  };

  const isDirectVideoLink = (url) => {
    return url && /\.(mp4|webm|ogg|mov)$/i.test(url);
  };

  if (!tutorial) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <div className="bg-gray-900">
        <div className="flex justify-between items-center p-4 border-b border-gray-700">
          <div className="flex-1 pr-4">
            <h3 className="text-white text-base font-semibold">{tutorial.title}</h3>
            {tutorial.description && (
              <p className="text-gray-400 text-xs mt-1 line-clamp-2">{tutorial.description}</p>
            )}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <a
              href={tutorial.link}
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-white transition-colors"
              title="Open in new tab"
            >
              <FiExternalLink size={18} />
            </a>
            <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
              <FiX size={20} />
            </button>
          </div>
        </div>

        <DialogContent className="p-0 bg-black">
          <div className="relative w-full" style={{ paddingTop: '56.25%' }}>
            {isDirectVideoLink(tutorial.link) ? (
              <video controls autoPlay className="absolute top-0 left-0 w-full h-full" src={tutorial.link}>
                Your browser does not support the video tag.
              </video>
            ) : embedUrl ? (
              <iframe
                className="absolute top-0 left-0 w-full h-full"
                src={embedUrl}
                title={tutorial.title}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                allowFullScreen
              />
            ) : (
              <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center bg-gray-900 text-white">
                <div className="text-center p-4">
                  <p className="mb-2 text-sm font-semibold">Unable to embed this video</p>
                  <p className="mb-4 text-xs text-gray-400">The video URL format is not supported for embedding</p>
                  <a
                    href={tutorial.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-3 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg inline-flex items-center gap-2 text-sm"
                  >
                    <FiExternalLink size={14} />
                    Open Video in New Tab
                  </a>
                </div>
              </div>
            )}
          </div>

          {/* Video Info */}
          <div className="bg-gray-900 text-white p-3 border-t border-gray-700">
            <div className="flex flex-wrap gap-3 text-xs">
              {tutorial.category && (
                <div className="flex items-center gap-1">
                  <span className="text-gray-400">Category:</span>
                  <span className="font-medium">{tutorial.category}</span>
                </div>
              )}
              {tutorial.level && (
                <div className="flex items-center gap-1">
                  <span className="text-gray-400">Level:</span>
                  <span className="font-medium capitalize">{tutorial.level}</span>
                </div>
              )}
              {tutorial.duration && (
                <div className="flex items-center gap-1">
                  <span className="text-gray-400">Duration:</span>
                  <span className="font-medium">{tutorial.duration}</span>
                </div>
              )}
            </div>
            {tutorial.tags && tutorial.tags.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {tutorial.tags.map((tag, index) => (
                  <span key={index} className="px-2 py-0.5 bg-gray-800 text-gray-300 rounded text-xs">
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </div>
    </Dialog>
  );
};

export default TutorialVideoPlayer;